import $i18n from 'panda-i18n';
import * as React from 'react';
import { useRef, useEffect, useState } from 'react';
import { animated, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { sleep } from '@/utils/sleep';
import { getScrollParent } from '@/utils/get-scroll-parent';
import { supportsPassive } from '@/utils/supports-passive';
import { rubberbandIfOutOfBounds } from '@/utils/rubberband';
const classPrefix = 'cn-ui-m-pull-to-refresh';
function getScrollTop(element) {
    return 'scrollTop' in element ? element.scrollTop : element.scrollY;
}
const CnPullToRefresh = (props) => {
    const headHeight = props.headHeight ?? 40;
    const threshold = props.threshold ?? 60;
    const [status, setStatus] = useState('pulling');
    const [springStyles, api] = useSpring(() => ({
        from: { height: 0 },
        config: {
            tension: 300,
            friction: 30,
            clamp: true,
        },
    }));
    const elementRef = useRef(null);
    const pullingRef = useRef(false);
    // 防止下拉时抖动
    useEffect(() => {
        elementRef.current?.addEventListener('touchmove', () => { });
    }, []);
    async function doRefresh() {
        api.start({ height: headHeight });
        setStatus('refreshing');
        try {
            if (typeof props.onRefresh === 'function') {
                await props.onRefresh();
            }
            setStatus('complete');
        }
        catch (e) {
            api.start({
                to: async (next) => {
                    await next({ height: 0 });
                    setStatus('pulling');
                },
            });
            throw e;
        }
        if (props.completeDelay > 0) {
            await sleep(props.completeDelay);
        }
        api.start({
            to: async (next) => {
                await next({ height: 0 });
                setStatus('pulling');
            },
        });
    }
    useDrag((state) => {
        if (status === 'refreshing' || status === 'complete')
            return;
        const { event } = state;
        if (state.last) {
            pullingRef.current = false;
            if (status === 'canRelease') {
                doRefresh();
            }
            else {
                api.start({ height: 0 });
            }
            return;
        }
        const [, y] = state.movement;
        if (state.first && y > 0) {
            const { target } = state.event;
            if (!target || !(target instanceof Element))
                return;
            let scrollParent = getScrollParent(target);
            while (true) {
                if (!scrollParent)
                    return;
                const scrollTop = getScrollTop(scrollParent);
                if (scrollTop > 0) {
                    return;
                }
                if (scrollParent instanceof Window) {
                    break;
                }
                scrollParent = getScrollParent(scrollParent.parentNode);
            }
            pullingRef.current = true;
        }
        if (!pullingRef.current)
            return;
        if (event.cancelable) {
            event.preventDefault();
        }
        event.stopPropagation();
        const height = Math.max(rubberbandIfOutOfBounds(y, 0, 0, headHeight * 5, 0.5), 0);
        api.start({ height });
        setStatus(height > threshold ? 'canRelease' : 'pulling');
    }, {
        pointer: { touch: true },
        axis: 'y',
        target: elementRef,
        enabled: !props.disabled,
        eventOptions: supportsPassive
            ? { passive: false }
            : false,
    });
    const renderStatusText = () => {
        if (props.renderText) {
            return props.renderText?.(status);
        }
        const { canReleaseText = $i18n.get({
            id: 'ReleaseImmediateRefresh',
            dm: '释放立即刷新',
            ns: 'CnPullToRefresh',
        }), completeText = $i18n.get({
            id: 'RefreshSuccessful',
            dm: '刷新成功',
            ns: 'CnPullToRefresh',
        }), pullingText = $i18n.get({
            id: 'PulldownRefresh',
            dm: '下拉刷新',
            ns: 'CnPullToRefresh',
        }), refreshingText = $i18n.get({
            id: 'Loading',
            dm: '加载中……',
            ns: 'CnPullToRefresh',
        }), } = props;
        if (status === 'pulling')
            return pullingText;
        if (status === 'canRelease')
            return canReleaseText;
        if (status === 'refreshing')
            return refreshingText;
        if (status === 'complete')
            return completeText;
    };
    return (React.createElement(animated.div, { ref: elementRef, className: classPrefix },
        React.createElement(animated.div, { style: springStyles, className: `${classPrefix}-head` },
            React.createElement("div", { className: `${classPrefix}-head-content`, style: { height: headHeight } }, renderStatusText())),
        React.createElement("div", { className: `${classPrefix}-content` }, props.children)));
};
CnPullToRefresh.displayName = 'CnPullToRefresh';
CnPullToRefresh.defaultProps = {
    completeDelay: 500,
    disabled: false,
    headHeight: 40,
    threshold: 60,
};
export { CnPullToRefresh };
